<template>
  <div class="mk-page">
    <MarketplaceHeader />

    <!-- Hero / search -->
    <section class="mk-hero">
      <div class="container">
        <template v-if="vendor">
          <div class="d-flex align-items-center" style="gap: 16px;">
            <img v-if="vendor.logo_url" :src="vendor.logo_url" :alt="vendor.name" class="mk-vendor-logo" />
            <div>
              <h1 class="mk-title mb-1">{{ vendor.name }}</h1>
              <p class="mk-sub mb-0">
                <i class="fe fe-map-pin mr-1"></i>{{ vendor.state || "Nigeria" }}
                <span class="mx-2">·</span>{{ vendor.products_count }} products
              </p>
            </div>
          </div>
          <p class="mk-sub mt-3 mb-0" v-if="vendor.description">{{ vendor.description }}</p>
        </template>
        <template v-else>
          <h1 class="mk-title">Hospital equipment &amp; supplies, straight from vendors</h1>
          <p class="mk-sub">Medical equipment, consumables, laboratory and diagnostic supplies for health facilities.</p>
        </template>

        <form class="mk-search mt-4" @submit.prevent="search(1)" role="search">
          <label for="mk-q" class="sr-only">Search products</label>
          <input id="mk-q" v-model="filters.q" type="search" class="form-control" placeholder="Search products, brands, SKUs…" />
          <button type="submit" class="btn mk-btn"><i class="fe fe-search mr-1"></i>Search</button>
        </form>

        <div class="mk-scope mt-3" v-if="buyerAgency">
          <span class="mr-2">Showing:</span>
          <button type="button" class="mk-chip" :class="{ active: !agencyScope }" @click="setScope(false)">All vendors</button>
          <button type="button" class="mk-chip" :class="{ active: agencyScope }" @click="setScope(true)">
            {{ buyerAgency.firstname || buyerAgency.agency_name }} marketplace
          </button>
        </div>
      </div>
    </section>

    <section class="container py-4">
      <RecommendedOffers v-if="!vendor" />
      <div class="row">
        <!-- Filters -->
        <aside class="col-lg-3 mb-4">
          <div class="mk-panel">
            <h6 class="mk-panel-title">Category</h6>
            <button type="button" class="mk-cat" :class="{ active: !filters.category }" @click="setCategory('')">All categories</button>
            <button v-for="c in categories" :key="c" type="button" class="mk-cat" :class="{ active: filters.category === c }"
              @click="setCategory(c)">{{ c }}</button>

            <h6 class="mk-panel-title mt-4">Price (₦)</h6>
            <div class="d-flex" style="gap: 8px;">
              <label for="mk-min" class="sr-only">Minimum price</label>
              <input id="mk-min" v-model="filters.min_price" type="number" min="0" class="form-control form-control-sm" placeholder="Min" />
              <label for="mk-max" class="sr-only">Maximum price</label>
              <input id="mk-max" v-model="filters.max_price" type="number" min="0" class="form-control form-control-sm" placeholder="Max" />
            </div>

            <h6 class="mk-panel-title mt-4">Vendor location</h6>
            <label for="mk-state" class="sr-only">Vendor state</label>
            <input id="mk-state" v-model="filters.state" type="text" class="form-control form-control-sm" placeholder="e.g. Lagos" />

            <div class="custom-control custom-checkbox mt-3">
              <input id="mk-stock" v-model="filters.in_stock" type="checkbox" class="custom-control-input" />
              <label class="custom-control-label small" for="mk-stock">In stock only</label>
            </div>

            <button type="button" class="btn mk-btn btn-block mt-3" @click="search(1)">Apply</button>
            <button type="button" class="btn btn-link btn-block btn-sm" @click="resetFilters">Clear filters</button>
          </div>
        </aside>

        <!-- Results -->
        <div class="col-lg-9">
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap" style="gap: 8px;">
            <div class="text-muted small">
              <span v-if="loading">Loading…</span>
              <span v-else>{{ total }} product{{ total === 1 ? "" : "s" }}</span>
              <router-link v-if="vendor" to="/marketplace" class="ml-2">· All vendors</router-link>
            </div>
            <div>
              <label for="mk-sort" class="sr-only">Sort</label>
              <select id="mk-sort" v-model="filters.sort" class="form-control form-control-sm" @change="search(1)">
                <option value="">Newest</option>
                <option value="price_asc">Price: low to high</option>
                <option value="price_desc">Price: high to low</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>

          <div class="row">
            <div class="col-sm-6 col-xl-4 mb-4" v-for="p in products" :key="p.id">
              <div class="mk-card">
                <router-link :to="productLink(p)" class="mk-card-img">
                  <img v-if="p.image_urls && p.image_urls.length" :src="p.image_urls[0]" :alt="p.name" loading="lazy" />
                  <i v-else class="fe fe-package"></i>
                </router-link>
                <div class="mk-card-body">
                  <div class="mk-cat-label">{{ p.category }}</div>
                  <router-link :to="productLink(p)" class="mk-card-name">{{ p.name }}</router-link>
                  <div class="mk-card-vendor">
                    <router-link :to="vendorLink(p.vendor)">{{ p.vendor && p.vendor.name }}</router-link>
                    <span v-if="p.vendor && p.vendor.state"> · {{ p.vendor.state }}</span>
                  </div>
                  <div class="mk-price">{{ money(p.unit_price) }} <small>/ {{ p.unit }}</small></div>
                  <div class="small" :class="p.quantity_available > 0 ? 'text-success' : 'text-danger'">
                    {{ p.quantity_available > 0 ? p.quantity_available + " in stock" : "Out of stock" }}
                    <span v-if="p.min_order_quantity > 1" class="text-muted"> · min {{ p.min_order_quantity }}</span>
                  </div>
                  <button type="button" class="btn mk-btn btn-block btn-sm mt-3" :disabled="p.quantity_available < p.min_order_quantity"
                    @click="addToCart(p)">
                    <i class="fe fe-shopping-cart mr-1"></i>Add to cart
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="!loading && !products.length" class="text-center text-muted py-5">
            <i class="fe fe-inbox" style="font-size: 2.5rem; opacity: .3;"></i>
            <p class="mt-2">No products match your search.</p>
          </div>

          <nav v-if="lastPage > 1" class="d-flex justify-content-center mt-2" aria-label="Product pages">
            <button type="button" class="btn btn-sm btn-outline-secondary mr-2" :disabled="page <= 1" @click="search(page - 1)">Previous</button>
            <span class="align-self-center small text-muted">Page {{ page }} of {{ lastPage }}</span>
            <button type="button" class="btn btn-sm btn-outline-secondary ml-2" :disabled="page >= lastPage" @click="search(page + 1)">Next</button>
          </nav>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import MarketplaceHeader from "./MarketplaceHeader.vue";
import cart from "./cart";
import RecommendedOffers from "../assessments/RecommendedOffers.vue";

function storedJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch (e) {
    return null;
  }
}

const emptyFilters = () => ({ q: "", category: "", min_price: "", max_price: "", state: "", in_stock: false, sort: "" });

export default {
  components: { MarketplaceHeader, RecommendedOffers },
  data() {
    const user = storedJson("user");
    const agency = storedJson("agency");
    const isBuyer = user && (user.type === "provider" || user.type === "provider_employee");
    return {
      categories: [],
      products: [],
      total: 0,
      page: 1,
      lastPage: 1,
      loading: false,
      vendor: null,
      filters: { ...emptyFilters(), q: this.$route.query.q || "", category: this.$route.query.category || "" },
      // Facilities can narrow the catalogue to vendors their agency approved.
      buyerAgency: isBuyer && agency && agency.id ? agency : null,
      agencyScope: false,
    };
  },
  computed: {
    vendorId() {
      return this.$route.query.vendor || null;
    },
    agencyId() {
      return this.agencyScope && this.buyerAgency ? this.buyerAgency.id : null;
    },
  },
  watch: {
    "$route.query.vendor"() {
      this.loadVendor();
      this.search(1);
    },
  },
  created() {
    this.axios.get("/api/v1/marketplace/categories").then((res) => (this.categories = res.data.data || []));
    this.loadVendor();
    this.search(1);
  },
  methods: {
    money(v) {
      return "₦" + Number(v || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 });
    },
    productLink(p) {
      return { path: `/marketplace/products/${p.id}`, query: this.agencyId ? { agency: this.agencyId } : {} };
    },
    vendorLink(v) {
      return v ? { path: "/marketplace", query: { vendor: v.id } } : "/marketplace";
    },
    loadVendor() {
      this.vendor = null;
      if (!this.vendorId) return;
      this.axios
        .get(`/api/v1/marketplace/vendors/${this.vendorId}`)
        .then((res) => (this.vendor = res.data.data))
        .catch(() => this.$router.replace("/marketplace"));
    },
    search(page) {
      this.loading = true;
      const params = { page, per_page: 24 };
      Object.entries(this.filters).forEach(([k, v]) => {
        if (v !== "" && v !== false && v !== null) params[k] = v === true ? 1 : v;
      });
      if (this.vendorId) params.vendor_id = this.vendorId;
      if (this.agencyId) params.agency_id = this.agencyId;

      this.axios
        .get("/api/v1/marketplace/products", { params })
        .then((res) => {
          this.products = res.data.data || [];
          this.total = res.data.total || 0;
          this.page = res.data.current_page || 1;
          this.lastPage = res.data.last_page || 1;
        })
        .catch(() => {
          this.products = [];
          this.$toasted.error("Could not load products", { position: "top-center", duration: 3000 });
        })
        .finally(() => (this.loading = false));
    },
    setCategory(c) {
      this.filters.category = c;
      this.search(1);
    },
    setScope(agencyOnly) {
      this.agencyScope = agencyOnly;
      this.search(1);
    },
    resetFilters() {
      this.filters = emptyFilters();
      this.search(1);
    },
    addToCart(p) {
      cart.add(p, p.min_order_quantity);
      this.$toasted.success(`${p.name} added to cart`, { position: "top-center", duration: 2000 });
    },
  },
};
</script>

<style scoped>
.mk-page { background: #f1f5f9; min-height: 100vh; }
.mk-hero { background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%); color: #fff; padding: 28px 0 32px; }
.mk-title { font-size: 1.6rem; font-weight: 700; }
.mk-sub { opacity: .85; }
.mk-vendor-logo { width: 64px; height: 64px; object-fit: cover; border-radius: 12px; background: #fff; }
.mk-search { display: flex; gap: 8px; max-width: 640px; }
.mk-search .form-control { border-radius: 10px; border: none; height: 44px; }
.mk-btn { background: #1e3a5f; color: #fff; border-radius: 8px; font-weight: 600; }
.mk-hero .mk-btn { background: #f59e0b; color: #1e293b; }
.mk-btn:hover { opacity: .9; color: #fff; }
.mk-btn:disabled { opacity: .5; }
.mk-scope { font-size: .85rem; }
.mk-chip { border: 1px solid rgba(255,255,255,.5); background: transparent; color: #fff; border-radius: 20px; padding: 3px 12px; margin-right: 6px; }
.mk-chip.active { background: #fff; color: #1e3a5f; font-weight: 600; }
.mk-panel { background: #fff; border-radius: 12px; padding: 18px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.mk-panel-title { font-size: .75rem; text-transform: uppercase; letter-spacing: .05em; color: #64748b; font-weight: 700; }
.mk-cat { display: block; width: 100%; text-align: left; border: none; background: none; padding: 5px 8px; border-radius: 6px; font-size: .86rem; color: #334155; }
.mk-cat:hover { background: #f1f5f9; }
.mk-cat.active { background: #dbeafe; color: #1d4ed8; font-weight: 600; }
.mk-card { background: #fff; border-radius: 12px; overflow: hidden; height: 100%; display: flex; flex-direction: column; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.mk-card-img { height: 170px; background: #f8fafc; display: flex; align-items: center; justify-content: center; color: #cbd5e1; font-size: 2.5rem; }
.mk-card-img img { width: 100%; height: 100%; object-fit: cover; }
.mk-card-body { padding: 14px; display: flex; flex-direction: column; flex: 1; }
.mk-cat-label { font-size: .7rem; text-transform: uppercase; color: #64748b; letter-spacing: .04em; }
.mk-card-name { font-weight: 600; color: #0f172a; margin: 2px 0; line-height: 1.3; }
.mk-card-vendor { font-size: .8rem; color: #64748b; margin-bottom: 8px; }
.mk-price { font-size: 1.1rem; font-weight: 700; color: #1e3a5f; margin-top: auto; }
.mk-price small { font-weight: 400; color: #64748b; font-size: .75rem; }
</style>
